<template>
  <aside
    class="conversation-list border rounded bg-body-emphasis"
    aria-label="Conversations"
  >
    <div class="p-2 border-bottom">
      <label :for="filterId" class="visually-hidden"
        >Filter conversations</label
      >
      <input
        :id="filterId"
        v-model="search"
        type="search"
        class="form-control form-control-sm"
        placeholder="Filter conversations"
        autocomplete="off"
      />
    </div>
    <button
      class="row-button"
      :class="{ selected: selectedId === null }"
      :aria-current="selectedId === null ? 'true' : undefined"
      @click="$emit('select', null)"
    >
      <strong class="small">All conversations</strong>
      <div v-if="status === 'ready'" class="text-muted small">
        {{ total }} {{ total === 1 ? "conversation" : "conversations" }}
      </div>
      <div
        v-if="status === 'ready' && total > conversations.length"
        class="text-muted small"
      >
        Showing the {{ conversations.length }} most recent — filter to find
        older ones
      </div>
    </button>
    <p
      v-if="status === 'loading' && !conversations.length"
      class="p-3 mb-0 small"
      role="status"
    >
      Loading conversations…
    </p>
    <div v-else-if="status === 'error'" class="p-3 small" role="alert">
      Could not load conversations.
      <button class="btn btn-link btn-sm p-0" @click="$emit('retry')">
        Try again
      </button>
    </div>
    <p
      v-else-if="status === 'ready' && !conversations.length"
      class="p-3 mb-0 small text-muted"
    >
      No conversations match.
    </p>
    <button
      v-for="conversation in conversations"
      :key="`${conversation.source}:${conversation.conversation_id}`"
      class="row-button"
      :class="{ selected: conversation.conversation_id === selectedId }"
      :aria-current="
        conversation.conversation_id === selectedId ? 'true' : undefined
      "
      :title="conversation.conversation_name ?? conversation.conversation_id"
      @click="$emit('select', conversation.conversation_id)"
    >
      <div class="d-flex justify-content-between align-items-baseline gap-2">
        <strong class="small text-truncate">{{
          conversation.conversation_name ?? conversation.conversation_id
        }}</strong>
        <time
          class="text-muted small text-nowrap"
          :datetime="conversation.last_message_at"
          >{{ lastActivity(conversation.last_message_at) }}</time
        >
      </div>
      <div class="d-flex align-items-center gap-2 text-muted small">
        <span>{{ conversation.message_count.toLocaleString() }} messages</span>
        <span
          v-if="conversation.trade_signal_message_count"
          class="badge bg-success-subtle text-success-emphasis"
          :title="`${conversation.trade_signal_message_count} messages with trade signals`"
          >{{
            conversation.trade_signal_message_count.toLocaleString()
          }}
          signals</span
        >
      </div>
      <div
        v-if="conversation.senders.length"
        class="text-muted small text-truncate"
      >
        {{ conversation.senders.slice(0, 3).join(", ")
        }}{{
          conversation.sender_count > 3
            ? ` +${conversation.sender_count - 3}`
            : ""
        }}
      </div>
    </button>
  </aside>
</template>
<script setup lang="ts">
import { useId } from "vue";
import type { ChatConversation } from "./tradeChatLogsTypes";

defineProps<{
  conversations: ChatConversation[];
  total: number;
  status: "idle" | "loading" | "ready" | "error";
  selectedId: string | null;
}>();
defineEmits<{
  select: [conversationId: string | null];
  retry: [];
}>();
const search = defineModel<string>("search", { required: true });

const filterId = useId();

/** Clock time for today's activity, a short date otherwise. */
function lastActivity(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toDateString() === new Date().toDateString()
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { day: "numeric", month: "short" });
}
</script>
<style scoped lang="scss">
.conversation-list {
  @media (min-width: 901px) {
    max-height: calc(100vh - 12rem);
    overflow-y: auto;
    position: sticky;
    top: 1rem;
  }
}
.row-button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.6rem 0.85rem;
  border: 0;
  border-bottom: 1px solid var(--phoenix-border-color);
  background: transparent;
  color: inherit;

  &:hover {
    background: var(--phoenix-secondary-bg);
  }
  &.selected {
    background: var(--phoenix-primary-bg-subtle);
    box-shadow: inset 3px 0 0 var(--phoenix-primary);
  }
}
</style>
